import type * as hast from 'hast';

import type { ContentRef } from '@gitbook/api-client';

import { validateAnyContentReference } from '../../../../../app-content-refs/src';

/**
 * Transform a map of data properties in a key:value map.
 *
 * e.g. { 'hello': 'world' } => { hello: 'world' }
 * e.g. { 'foo-0': 'bar', 'foo-1': 'baz' } => { foo: ['bar', 'baz'] }
 */
export function fromDataAttributes(
    properties: object,
    acceptedPropertyKeys: string[]
): { [k: string]: any } {
    const arrayRegex = /(.+)-[0-9]+/g;
    return Object.keys(properties).reduce((obj, key) => {
        let parsedKey = acceptedPropertyKeys.find(
            (acceptedKey) =>
                acceptedKey.toLowerCase() === key ||
                `data${capitalizeFirstLetter(acceptedKey)}` === key
        );
        if (parsedKey) {
            obj[parsedKey] = properties[key];
        } else if (arrayRegex.test(key)) {
            // parse arrays
            arrayRegex.lastIndex = 0;

            parsedKey = acceptedPropertyKeys.find(
                (acceptedKey) =>
                    key.startsWith(acceptedKey.toLowerCase()) ||
                    key.startsWith(`data${capitalizeFirstLetter(acceptedKey)}`)
            )!;

            if (!obj[parsedKey]) {
                obj[parsedKey] = [];
            }
            obj[parsedKey].push(properties[key]);
        }

        return obj;
    }, {});
}

/**
 * Capitalize the first letter of a string.
 */
function capitalizeFirstLetter(string: string): string {
    return string.charAt(0).toUpperCase() + string.slice(1).toLowerCase();
}

/**
 * Test if a hast element has a boolean attribute.
 */
export function getBooleanAttribute(
    properties: hast.Properties | undefined,
    propName: string
): boolean {
    return !!(
        properties &&
        propName in properties &&
        properties[propName] !== false &&
        properties[propName] !== 'false'
    );
}

/**
 * Test if a hast element has a string attribute.
 */
export function getStringAttribute(
    properties: hast.Properties | undefined,
    propName: string
): string | undefined {
    let value = properties?.[propName];

    if (!value) {
        return undefined;
    }

    if (Array.isArray(value)) {
        value = value[0];
    }

    if (typeof value === 'string') {
        return value;
    }

    return undefined;
}

/**
 * Test if a hast element has an array of string as an attribute.
 */
export function getStringsAttribute(
    properties: hast.Properties | undefined,
    propName: string
): string[] {
    const value = properties?.[propName];
    if (!value) {
        return [];
    }

    if (Array.isArray(value)) {
        return value.map((v) => v.toString());
    }

    if (typeof value === 'string') {
        return [value];
    }

    return [];
}

/**
 * Split class names into an array of strings.
 */
export function getClassNamesAttribute(properties: hast.Properties | undefined): string[] {
    const values = getStringsAttribute(properties, 'className');
    return values.join(' ').toLowerCase().split(/\s+/);
}

/**
 * Return an hast attribute as a number
 */
export function getNumberAttribute<DefaultValue extends number | undefined>(
    properties: hast.Properties | undefined,
    propName: string,
    defaultValue?: DefaultValue
): number | DefaultValue {
    if (!properties || !(propName in properties)) {
        // @ts-ignore
        return defaultValue;
    }

    const value = Number(properties[propName]);

    // @ts-ignore
    return Number.isNaN(value) ? defaultValue : value;
}

/**
 * Return an hast attribute as a string enum.
 */
export function getEnumAttribute<
    Value extends string,
    DefaultValue extends Value | undefined = undefined,
>(
    properties: hast.Properties | undefined,
    propName: string,
    values: Value[],
    defaultValue?: DefaultValue
): Value | DefaultValue {
    if (!properties || !(propName in properties)) {
        // @ts-ignore
        return defaultValue;
    }

    const value = properties[propName];

    // @ts-ignore
    return values.includes(value) ? value : defaultValue;
}

export function getContentRefAttribute(
    properties: hast.Properties | undefined,
    propName: string
): ContentRef | undefined {
    const value = properties?.[propName];
    if (!value || !validateAnyContentReference(value)) {
        return undefined;
    }

    return value;
}
